import { type FragmentOf, type ResultOf, readFragment } from "@opensea/graphql"
import { useHandoff } from "@opensea/navigation"
import { Button, Image } from "@opensea/next-ui-kit"
import {
  Container,
  classNames,
  Flex,
  GradientMask,
  isTypingInInputField,
  useCarousel,
} from "@opensea/ui-kit"
import { useEventListener } from "@opensea/ui-kit/hooks"
import { ArrowLeftAlt, ArrowRightAlt } from "@opensea/ui-kit/icons"
import { type RefCallback, useEffect, useRef, useState } from "react"
import { useSwipeable } from "react-swipeable"
import { ItemViewModalFragment } from "./ItemViewModal.fragment"

export type ThumbnailPickerItem = ResultOf<typeof ItemViewModalFragment>

type ThumbnailPickerProps = {
  className?: string
  initialTokenId?: string
  onChange: (item: ThumbnailPickerItem) => void
}

export function ThumbnailPicker(props: ThumbnailPickerProps) {
  const { className, ...rest } = props
  const items =
    useHandoff<(FragmentOf<typeof ItemViewModalFragment> | undefined)[]>()

  return (
    <Flex className={className}>
      {/*
          If we weren't given a handoff, then don't show the picker
          For example, on activity page there is no handoff but on item collections there is.
        */}
      {Array.isArray(items) ? (
        <ThumbnailPickerWithItems
          {...rest}
          items={items.flatMap(item => (item ? [item] : []))}
        />
      ) : null}
    </Flex>
  )
}

function ThumbnailPickerWithItems({
  initialTokenId,
  items: itemKeys,
  onChange,
}: ThumbnailPickerProps & {
  items: FragmentOf<typeof ItemViewModalFragment>[]
}) {
  const items = itemKeys.map(itemKey =>
    readFragment(ItemViewModalFragment, itemKey),
  )
  const [selectedIndex, setSelectedIndex] = useState(
    items.findIndex(item => item.tokenId === initialTokenId),
  )
  const [emblaThumbsRef, emblaThumbsApi] = useCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
  })

  useEffect(() => {
    emblaThumbsApi?.scrollTo(selectedIndex)
  }, [emblaThumbsApi, selectedIndex])

  function handleSelect(
    item: ResultOf<typeof ItemViewModalFragment>,
    index: number,
  ) {
    setSelectedIndex(index)
    onChange(item)
  }

  function handlePrev() {
    const index = Math.max(0, selectedIndex - 1)
    const item = items[index]
    setSelectedIndex(index)
    onChange(item)
  }

  function handleNext() {
    const index = Math.min(items.length - 1, selectedIndex + 1)
    const item = items[index]
    setSelectedIndex(index)
    emblaThumbsApi?.scrollTo(index)
    onChange(item)
  }

  useEventListener("keydown", event => {
    // Don't execute hotkeys if the user is currently typing in an input field
    if (isTypingInInputField()) {
      return
    }

    if (event.key === "ArrowLeft" || event.key === "j") {
      handlePrev()
    } else if (event.key === "ArrowRight" || event.key === "k") {
      handleNext()
    }
  })

  const scrollableElementsCache = useRef(new WeakSet<Element>())

  function checkForHorizontalScroll(element: Element) {
    if (scrollableElementsCache.current.has(element)) {
      return true
    }

    const computedStyle = window.getComputedStyle(element)
    const hasHorizontalScroll =
      computedStyle.overflowX === "auto" ||
      computedStyle.overflowX === "scroll" ||
      element.scrollWidth > element.clientWidth

    if (hasHorizontalScroll) {
      scrollableElementsCache.current.add(element)
      return true
    }

    const parent = element.parentElement
    if (parent && parent !== document.body) {
      return checkForHorizontalScroll(parent)
    }

    return false
  }

  const { ref: documentRef } = useSwipeable({
    onSwipedLeft: eventData => {
      const target = eventData.event.target
      if (target instanceof Element && !checkForHorizontalScroll(target)) {
        handleNext()
      }
    },
    onSwipedRight: eventData => {
      const target = eventData.event.target
      if (target instanceof Element && !checkForHorizontalScroll(target)) {
        handlePrev()
      }
    },
    delta: 150,
  }) as { ref: RefCallback<Document> }

  useEffect(() => {
    documentRef(document)

    return () => {
      // @ts-expect-error - This is intentional to clean up refs
      documentRef({})
    }
  }, [documentRef])

  return (
    <Flex className="w-full items-center justify-center gap-7">
      <div className="hidden grow-0 md:flex">
        <Button
          aria-label="Prev"
          className="focus:routline-hidden"
          disabled={selectedIndex === 0}
          icon={ArrowLeftAlt}
          onClick={handlePrev}
          variant="secondary"
        />
      </div>

      <GradientMask className="flex size-full items-center overflow-x-hidden md:max-w-[60vw] lg:max-w-[600px]">
        <Container className="py-0.5">
          <div className="touch-pan-y" ref={emblaThumbsRef}>
            <div className="flex">
              {items.map((item, index) => (
                <Thumb
                  image={item.imageUrl || ""}
                  key={item.id}
                  onClick={() => {
                    handleSelect(item, index)
                  }}
                  selected={index === selectedIndex}
                />
              ))}
            </div>
          </div>
        </Container>
      </GradientMask>

      <div className="hidden grow-0 md:flex">
        <Button
          aria-label="Next"
          className={classNames("focus:outline-hidden")}
          disabled={selectedIndex === items.length - 1}
          icon={ArrowRightAlt}
          iconSide="right"
          onClick={handleNext}
          variant="secondary"
        />
      </div>
    </Flex>
  )
}

type ThumbProps = {
  selected: boolean
  image: string
  onClick: () => void
}

export function Thumb({ selected, image, onClick }: ThumbProps) {
  return (
    <button
      className={classNames(
        "mr-1 flex size-10 shrink-0 grow-0 items-center justify-center overflow-hidden rounded bg-white sm:mr-2",
        selected ? "shadow-[0_0_0_2px_white]" : "border-transparent opacity-60",
      )}
      onClick={onClick}
      type="button"
    >
      <Image
        className="object-cover"
        frameTime={1}
        height={48}
        src={image}
        width={48}
      />
    </button>
  )
}
